import * as React from "react";

// Third Party
import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";

// helpers
import { useAccessPermission } from "@/helpers/acessPermission";


// UI
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Button } from "@/components/ui/button";
import DinamixSkeletonTable from "@/components/skeleton/DinamixTableSkeleton";
import {
  Card,
  CardHeader,
  CardTitle,
  CardDescription,
  CardContent,
} from "@/components/ui/card";

import { useToast } from "@/components/ui/use-toast";
import calculateDaysFromNow from "@/utils/calculateDaysFromNow";
import { RequireEditDocs } from "@/services/require-edit-docs";

import NumberFormatSelector from "@/components/NumberFormatSelector";
import FileHandleViewer from "@/components/FileHandleViewer";

// Icons
import { PencilLine, ArrowLeft } from "lucide-react";

// Services and Utils
import { getFixAsset } from "@/services/finance";
import { type FixAssetDetail } from "@/types/finance";

import formatDate from "@/utils/formatDate";

// modal
import { ModalRequireEditDoc } from "@/components/ModalRequireEditDoc";


export const Route = createFileRoute(
  "/_layout-private/report/finance/company/_layout/fix-assets/"
)({
  component: RouteComponent,
});

function RouteComponent() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { toast } = useToast();
  const companyId = localStorage.getItem("companyId");
  const year = Number(localStorage.getItem("year"));
  const [unit, setUnit] = React.useState("1");
  const [open, setOpen] = React.useState(false);
  const { company_financial_report } = useAccessPermission();
  React.useEffect(() => {
    if (!year) {
      navigate({ to: "/report/finance/company/fix-assets/add" });
    }
  }, [year, navigate]);

  const { data, isLoading, error } = useQuery({
    queryKey: ["fix-assets", companyId, year],
    queryFn: () => getFixAsset(companyId as string, year),
    enabled: Boolean(companyId && year),
  });

  const dayFromNow = calculateDaysFromNow(data?.data?.createdAt);


  const requireEditMutation = useMutation({
    mutationFn: (data: {
      editId: string;
      editRequestType: string;
      pendingReason: string;
      documentFile: string;
      editRequestStatus: string;
    }) => RequireEditDocs(data),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ["fix-assets", companyId, year],
      });
      toast({
        title: "ສຳເລັດ",
        description: "ທ່ານໄດ້ສົ່ງຄໍາຮ້ອງຂໍແກ້ໄຂເອກະສານແລ້ວ",
        duration: 5000,
        variant: "success",
      });
      setOpen(false);
    },
    onError: () => {
      toast({
        title: "ຜິດພາດ",
        description:
          "ການສົ່ງຄໍາຮ້ອງຂໍແກ້ໄຂເອກະສານ ບໍ່ສາມາດເຮັດໄດ້ ກະລຸນາລອງໃໝ່ ຫຼື ຕິດຕໍ່ ຜູ້ໃຊ້ ຖ້າ ວ່າ ຍັງ ມີ ບັນ ຫາ",
        duration: 5000,
        variant: "destructive",
      });
    },
  });

  React.useEffect(() => {
    if (!data?.data?.id && !isLoading) {
      navigate({ to: "/report/finance/company/fix-assets/add" });
    }
  }, [data, navigate, isLoading]);

  if (!companyId || !year) {
    return <div>Missing required data</div>;
  }

  if (isLoading) {
    return <DinamixSkeletonTable />;
  }
  if (error) return <div>Error loading data</div>;

  const fixAssetData = data?.data;
  const fixAssetDetail: FixAssetDetail[] = fixAssetData?.fixAssetDetails;

  // Function

  const formatNumberWithCommas = (value: number) => {
    // Convert value to a string and remove any existing commas
    const stringValue = String(value || "")?.replace(/,/g, "");

    // Check if the value is negative
    const isNegative = stringValue.startsWith("-");

    // Remove any non-numeric characters (such as parentheses) and handle negative values
    const numericValue = isNegative
      ? stringValue.slice(1)
      : stringValue?.replace(/[()]/g, "");

    // Convert the numeric value to a number
    const numericAmount = Number(numericValue);

    // If the value is invalid (NaN), return the original string value
    if (Number.isNaN(numericAmount)) {
      return value;
    }

    // Format the numeric value with commas
    const formattedValue = numericAmount?.toLocaleString();

    // If it's negative, wrap it in parentheses
    return isNegative ? `(${formattedValue})` : formattedValue;
  };

  function parseParenthesisNumber(strValue: string | number) {
    if (!strValue) return 0;
    const stringValue = String(strValue)?.replace(/,/g, ""); // remove commas

    const isNegative = stringValue.startsWith("(") && stringValue.endsWith(")");
    const numericValue = isNegative ? stringValue.slice(1, -1) : stringValue;

    const parsed = Number(numericValue) || 0;
    return isNegative ? -parsed : parsed;
  }

  function computeSumForField(data: any[], fieldName: string) {
    const sum = data?.reduce((acc, row) => {
      return acc + parseParenthesisNumber(row[fieldName]);
    }, 0);
    return sum?.toLocaleString();
  }

  const handleRequireEdit = ({
    reason,
    file,
  }: {
    reason: string;
    file: string;
  }) => {
    if (!fixAssetData?.id) return;
    requireEditMutation.mutate({
      editId: fixAssetData?.id,
      editRequestType: "FIX_ASSET_REPORT",
      pendingReason: reason,
      documentFile: file,
      editRequestStatus: "REQUEST",
    });
  };

  return (
    <section>
      <Card className="border-none">
        <CardHeader className="border-b mb-[32px]">
          <button
            type="button"
            onClick={() =>
              navigate({
                to: "/report/finance",
                search: {
                  page: 1,
                  startYear: 0,
                  endYear: 0,
                },
              })
            }
            className="flex items-center gap-[4px]  mb-4"
          >
            <ArrowLeft className="w-5 h-5" />
            ກັບຄືນ
          </button>

          <CardTitle>ລາຍລະອຽດເອກະສານທາງການເງິນງວດປີ {year}</CardTitle>
          <CardDescription>ລາຍລະອຽດ</CardDescription>

          <div className="flex justify-end w-full mt-5 mb-5 gap-x-2">
            {dayFromNow <= 3 ? company_financial_report.isUpdate && (
              <Button
                onClick={() =>
                  navigate({
                    to: "/report/finance/company/fix-assets/edit",
                  })
                }
                variant="secondary"
                className="flex items-center border border-blue-500 bg-none gap-x-1 w-[120px] rounded-md  p-2 text-blue-500"
              >
                <PencilLine /> ແກ້ໄຂ
              </Button>
            )
              :
              company_financial_report.isUpdate && (
                fixAssetData?.editRequestStatus === "REQUEST" ? (
                  <p className="text-red-500">ກະລຸນາລໍຖ້າການກວດສອບ</p>
                ) : (
                  <Button
                    onClick={() => setOpen(true)}
                    variant="secondary"
                    className="flex items-center border border-blue-500 gap-x-1 w-[120px] p-2 text-blue-500"
                  >
                    <PencilLine /> ຂໍແກ້ໄຂເອກະສານ
                  </Button>
                ))}
          </div>
          <ModalRequireEditDoc
            isOpen={open}
            onClose={() => setOpen(false)}
            onConfirm={handleRequireEdit}
          />
        </CardHeader>
        <CardContent>
          <div className="mb-4">
            <NumberFormatSelector unit={unit} setUnit={setUnit} />
          </div>

          <div>
            {isLoading ? (
              <DinamixSkeletonTable rows={5} columns={3} />
            ) : (
              <Table className="mb-4">
                <TableHeader>
                  <TableRow className="[&>*]:border [&>*]:w-[180px] [&>*]:whitespace-nowrap  [&>*]:font-bold [&>*]:text-black">
                    <TableHead style={{ width: 220 }}>ເນຶ້ອໃນລາຍງານ</TableHead>
                    <TableHead>ຈຳນວນ</TableHead>
                    <TableHead>ຫົວໜ່ວຍ</TableHead>
                    <TableHead>ວ.ດ.ປ ຮັບເຂົ້າ</TableHead>
                    <TableHead>ລວມອາຍຸ</TableHead>
                    <TableHead>ມູນຄ່າເດີມ</TableHead>
                    <TableHead>ຄ່າຫຼຸ້ຍຫ້ຽນສະສົມ</TableHead>
                    <TableHead>ມູນຄ່າຍັງເຫຼືອ</TableHead>
                    <TableHead>ມູນຄ່າສະສາງ </TableHead>
                    <TableHead>ວັນທີສະສາງ</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {/*  */}

                  {fixAssetDetail?.map((item) => (
                    <TableRow
                      key={item?.id}
                      className="[&>*]:border  [&>*]:whitespace-nowrap  "
                    >
                      <TableCell>{item?.name}</TableCell>
                      <TableCell className="text-right">
                        {formatNumberWithCommas(item?.count)}
                      </TableCell>
                      <TableCell>{item?.unit}</TableCell>
                      <TableCell>
                        {formatDate({
                          date: item?.receivedDate,
                          format: "DD/MM/YYYY",
                        })}
                      </TableCell>
                      <TableCell>{item?.unit}</TableCell>
                      <TableCell className="text-right">
                        {formatNumberWithCommas(item?.originalValue / Number(unit))}
                      </TableCell>
                      <TableCell className="text-right">
                        {formatNumberWithCommas(item?.depreciationValue / Number(unit))}
                      </TableCell>
                      <TableCell className="text-right">
                        {formatNumberWithCommas(item?.remainingValue / Number(unit))}
                      </TableCell>
                      <TableCell className="text-right">
                        {formatNumberWithCommas(item?.disposalValue / Number(unit))}
                      </TableCell>
                      <TableCell>
                        {formatDate({
                          date: item?.disposalDate,
                          format: "DD/MM/YYYY",
                        })}
                      </TableCell>
                    </TableRow>
                  ))}

                  <TableRow className="bg-gray-100 [&>*]:border">
                    <TableCell className="font-bold text-center">ລວມ</TableCell>
                    <TableCell className="text-right">
                      {computeSumForField(fixAssetDetail, "count") / Number(unit)}
                    </TableCell>
                    <TableCell />
                    <TableCell />
                    <TableCell />
                    <TableCell className="text-right">
                      {(
                        Number.parseFloat(
                          computeSumForField(
                            fixAssetDetail,
                            "originalValue"
                          )?.replace(/,/g, "")
                        ) / Number(unit)
                      ).toLocaleString()}
                    </TableCell>

                    <TableCell className="text-right">
                      {(
                        Number.parseFloat(
                          computeSumForField(
                            fixAssetDetail,
                            "depreciationValue"
                          )?.replace(/,/g, "")
                        ) / Number(unit)
                      ).toLocaleString()}
                    </TableCell>
                    <TableCell className="text-right">
                      {/* Sum of Remaining Value */}
                      {formatNumberWithCommas(
                        fixAssetDetail?.reduce(
                          (acc, asset) =>
                            acc +
                            (parseParenthesisNumber(asset.originalValue) -
                              parseParenthesisNumber(asset.depreciationValue)),
                          0
                        ) / Number(unit)
                      )}
                    </TableCell>
                    <TableCell className="text-right">
                      {/* Sum of Disposal Value */}
                      {computeSumForField(fixAssetDetail, "disposalValue") /
                        Number(unit)}
                    </TableCell>
                    <TableCell />
                  </TableRow>
                </TableBody>
              </Table>
            )}

            {fixAssetData?.docFile
              ? company_financial_report.isDownload && (
                <FileHandleViewer fileUrl={fixAssetData?.docFile} />
              )
              : null}
          </div>
        </CardContent>
      </Card>
    </section>
  );
}
